<template>
  <div>
    <b-modal ref="SalesOrderPositionModal"
             :title="modalTitle" :no-close-on-backdrop="true">
      <div slot="modal-title">
        <div class="d-flex">
          <h5 class="modal-title">{{modalTitle}}</h5>
        </div>
      </div>
      <div>
        <b-row>
          <b-col>
            <label>{{$t('modal_dialogs.sales_order_position.name')}}</label>
            <div class="d-flex">
              <input class="form-control" v-model="salesOrderItemData.name"
                     :class="{ required: salesOrderItemData.name === ''}"
                     :disabled="salesOrderItemData.related_invoice_item_exists">
              <div class="reply-icon-wrapper vertical-center-container"
                   @click="applySalesOrderItemNameFromStockProduct"
                   title="Set name from stock product"
                   :disabled="salesOrderItemData.related_invoice_item_exists">
                <icon name="reply" class="reply-icon"></icon>
              </div>
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('modal_dialogs.sales_order_position.stock_product')}}</label>
            <div class="d-flex">
              <multiselect v-model="salesOrderItemData.product_name" label="name" track-by="name"
                           placeholder="Search stock product" open-direction="bottom"
                           :options="stockProducts"
                           :multiple="false" :searchable="true" :loading="isLoadingStockProducts"
                           :internal-search="false"
                           :close-on-select="true" :max-height="200"
                           :hide-selected="false" @select="loadStockProductData"
                           :class="{ required: !salesOrderItemData.product_name}"
                           @search-change="asyncProductsSearch($event, 'product_name')"
                           :disabled="salesOrderItemData.related_invoice_item_exists">
                <span slot="noResult">{{$t('modal_dialogs.sales_order_position.no_result_text')}}</span>
              </multiselect>
              <div class="icons-group vertical-center-container"
                   @click="createProduct(salesOrderItemData.product_name)"
                   title="Edit/create stock product">
                <icon name="pencil-alt"></icon>
              </div>
            </div>
            <div class="stock-product-info">
              Quantity on stock: t - {{stockProductData.weight}}, pieces - {{stockProductData.pieces}}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('modal_dialogs.sales_order_position.package_amount')}}</label>
            <input type="number" class="form-control" v-model="salesOrderItemData.pack_count"/>
          </b-col>
          <b-col>
            <label>{{$t('modal_dialogs.sales_order_position.article')}}</label>
            <input class="form-control" v-model="salesOrderItemData.article_no"
                   :disabled="salesOrderItemData.related_invoice_item_exists"/>
          </b-col>
        </b-row>
        <b-row>
          <b-col class="d-flex">
            <b-col class="padding-left-0">
              <label>{{$t('modal_dialogs.sales_order_position.amount')}}</label>
              <input class="form-control" v-model="salesOrderItemData.product_amount" step="any"
                     :class="{ required: salesOrderItemData.product_amount === ''}"/>
            </b-col>
            <b-col class="d-flex padding-right-0">
              <multiselect class="align-self-bottom" v-model="salesOrderItemData.measurement_value"
                           :max-height="200" :searchable="true"
                           placeholder="" :show-labels="false"
                           label="name" track-by="name" :options="measurement_values"
                           :allowEmpty="false"></multiselect>
            </b-col>
          </b-col>
          <b-col class="d-flex">
            <b-col class="padding-left-0">
              <label style="padding-left: 10px;">{{$t('modal_dialogs.sales_order_position.sale')}}</label>
              <div class="d-flex">
                <input class="form-control" type="number" v-model="salesOrderItemData.sale_price"
                       step="any"
                       :class="{ required: salesOrderItemData.sale_price === ''}"
                       :disabled="salesOrderItemData.related_invoice_item_exists"/>
              </div>
            </b-col>
            <span class="align-self-bottom slash-symbol">/</span>
            <b-col class="d-flex padding-right-0">
              <multiselect class="align-self-bottom" v-model="salesOrderItemData.measurement_for_price"
                           :max-height="200" :searchable="true"
                           placeholder="" :show-labels="false"
                           label="name" track-by="name" :options="measurements"
                           :allowEmpty="false"
                           :disabled="salesOrderItemData.related_invoice_item_exists"></multiselect>
            </b-col>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('modal_dialogs.sales_order_position.package_type')}}</label>
            <multiselect class="align-self-bottom" v-model="salesOrderItemData.package_type"
                         :max-height="200" :searchable="true"
                         :show-labels="false"
                         label="name" track-by="name" :options="packageTypes"
                         :allowEmpty="false"
                         :class="{ required: !salesOrderItemData.package_type}"
                         :disabled="salesOrderItemData.related_invoice_item_exists"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.notes')}}</label>
            <b-form-textarea placeholder="Enter notes" v-model="salesOrderItemData.notes">
            </b-form-textarea>
          </b-col>
        </b-row>
        <b-row v-if="salesOrderItemData.package_type && salesOrderItemData.package_type.name === 'Bundles'">
          <b-col cols="6">
            <label>{{$t('modal_dialogs.sales_order_position.bundle_size')}}</label>
            <input class="form-control" type="number" v-model="salesOrderItemData.bundle_size"
                   :disabled="salesOrderItemData.related_invoice_item_exists">
          </b-col>
        </b-row>
      </div>
      <div slot="modal-footer" class="w-100 text-center">
        <b-btn size="sm" variant="success" @click="saveSaleOrderItem">
          {{$t('buttons.common.save')}}
        </b-btn>
        <b-btn variant="outline-success" v-if="!isNewSalesOrderItem" @click="createBasedOnChanges"
               style="margin-left: 70px;">
          {{$t('buttons.common.create_on_changes')}}
        </b-btn>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import Multiselect from 'vue-multiselect';
  import { mapGetters } from 'vuex';
  import { notificationsRenderer } from '@/helpers/renderHelpers';

  import {
    handlerResponse
  } from '@/helpers/commonHelpers';

  export default {
    props: ['id'],
    components: {
      Multiselect
    },
    data() {
      return {
        isLoadingStockProducts: false,
        isLoadingSaleProducts: false,
        isLoadingShippingProducts: false,
        isNewSalesOrderItem: true,
        measurements: [
          {name: 't', key: 'tonne'},
          {name: 'pcs', key: 'pieces'}
        ],
        measurement_values: [],
        requiredFields: ['name', 'product_name', 'sale_product', 'amount', 'price', 'package_type'],
        salesContractItems: [],
        stockProducts: [],
        saleProducts: [],
        shippingProducts: [],
        salesOrderItemData: {},
        stockProductData: {},
        salesOrderItemDataInitial: {
          name: '',
          product_name: null,
          pack_size: null,
          article_no: '',
          package_type: {},
          pack_count: null,
          stock_price: 0,
          sale_price: 0,
          measurement_value: {name: 't', key: 'tonne'},
          measurement_for_price: {name: 't', key: 'tonne'},
          floating_length: false,
          product_amount: 0,
          bundle_size: 1,
          notes: ''
        }
      }
    },
    methods: {
      asyncProductsSearch(query, type) {
        if (query) {
          const params = {
            limit: 50,
            offset: 0,
            text: query,
          };
          if (type === 'product_name') {
            this.isLoadingStockProducts = true;
            params.products_from_sales_contract_item = this.salesOrderItemData.sales_contract_item && this.salesOrderItemData.sales_contract_item.id;
            params.product_sizes = true;
          } else if (type === 'sale_product') {
            this.isLoadingSaleProducts = true;
            params.product_sizes = true;
            params.sale_products_from_sales_contract_item = this.salesOrderItemData.sales_contract_item && this.salesOrderItemData.sales_contract_item.id;
          }
          this.$http.get(`woodware/list/search_for_product_short_view/`, {params}).then(handlerResponse).then((response) => {
            if (type === 'product_name') {
              this.stockProducts = response.results;
              this.isLoadingStockProducts = false;
            } else if (type === 'sale_product') {
              this.saleProducts = response.results;
              this.isLoadingSaleProducts = false;
            } else if (type === 'shipment_product') {
              this.shippingProducts = response.results;
              this.isLoadingShippingProducts = false;
            }
          });
        }
      },
      selectSalesContractItem(salesContractItem) {
        const params = {id: salesContractItem.id};
        this.$http.get(`sales/sales_contract_items/`, {params}).then(handlerResponse).then((response) => {
          delete response[0]['id'];
          this.salesOrderItemData = {...this.salesOrderItemData, ...response[0]};
        })
      },
      applySalesOrderItemNameFromStockProduct() {
        if (this.salesOrderItemData.product_name) {
          this.salesOrderItemData.name = this.salesOrderItemData.product_name.name;
        }
      },
      applyProductFromStockProduct(productType) {
        this.salesOrderItemData[productType] = this.salesOrderItemData.product_name;
        this.convertStockPriceToSalePrice();
      },
      loadStockProductData(selectedStockProduct) {
        const params = {
          stock_data: true,
          id: selectedStockProduct.id
        };
        this.$http.get(`woodware/list/search_for_product_short_view/`, {params}).then(handlerResponse).then((response) => {
          this.stockProductData = response[0] && response[0].stock_data ? response[0].stock_data : [];
        })
      },
      createProduct(product) {
        const productId = product ? product.id : 'new';
        window.open(`/product/${productId}`, '_blank');
      },
      convertStockPriceToSalePrice() {
        if (this.salesOrderItemData.product_name && this.salesOrderItemData.sale_product) {
          const {width: productWidth, thickness: productThickness} = this.salesOrderItemData.product_name;
          const {width: saleProductWidth, thickness: saleProductThickness} = this.salesOrderItemData.sale_product;
          const productSizes = productWidth * productThickness,
            salesProductSizes = saleProductWidth * saleProductThickness;
          this.salesOrderItemData.sale_price = (this.salesOrderItemData.stock_price * productSizes / salesProductSizes).toFixed(2);
        }
      },
      prepareSavedData() {
        return {
          ...this.salesOrderItemData, ...{
            product_name: this.salesOrderItemData.product_name && this.salesOrderItemData.product_name.id,
            measurement_for_price: this.salesOrderItemData.measurement_for_price.key,
            measurement_value: this.salesOrderItemData.measurement_value.key,
            package_type: this.salesOrderItemData.package_type.id,
            pack_size: this.salesOrderItemData.pack_size === '' ? null : this.salesOrderItemData.pack_size,
            pack_count: this.salesOrderItemData.pack_count === '' ? null : this.salesOrderItemData.pack_count,
            product_amount: this.salesOrderItemData.product_amount === '' ? null : this.salesOrderItemData.product_amount,
            sales_order: this.id
          }
        }
      },
      createSalesOrderItem(savedData) {
        this.$http.post(`sales/salesorders/salesordersitems/`, savedData).then(handlerResponse).then((response) => {
          this.$refs['SalesOrderPositionModal'].hide();
          if (this.salesOrderItemData.isPackageData) {
            this.$store.dispatch('salesOrder/loadPackagesGroupingByOrderItems', {id: this.id});
            this.$store.dispatch('salesOrder/loadNonBoundedPackages', {id: this.id});
          } else {
            this.$store.dispatch('salesOrder/loadSalesOrderItems', {id: this.id});
          }
        }).catch((error) => {
          console.log(error);
          notificationsRenderer(error.data, 'error');
        });
      },
      saveSaleOrderItem() {
        if (this.validFormData) {
          const savedData = this.prepareSavedData();
          if (this.isNewSalesOrderItem) {
            this.createSalesOrderItem(savedData);
          } else {
            const salesOrderItemId = this.salesOrderItemData.id;
            this.$http.put(`sales/salesordersitems/${salesOrderItemId}/`, savedData).then(handlerResponse).then((response) => {
              this.$refs['SalesOrderPositionModal'].hide();
              this.$store.dispatch('salesOrder/loadSalesOrderItems', {id: this.id});
            }).catch((error) => {
              console.log(error);
              notificationsRenderer(error.data, 'error');
            });
          }
        } else {
          this.$swal.fire('', 'Fill required fields', 'error');
        }
      },
      createBasedOnChanges() {
        if (this.validFormData) {
          const savedData = this.prepareSavedData();
          const salesOrderItemId = this.salesOrderItemData.id;
          this.createSalesOrderItem(savedData, salesOrderItemId);
        } else {
          this.$swal.fire('', 'Fill required fields', 'error');
        }
      }
    },
    computed: {
      ...mapGetters('generic', {
        packageTypes: 'packageTypes',
      }),
      validFormData() {
        return this.requiredFields.filter((item) => {
          return this.salesOrderItemData[item] !== '' && this.salesOrderItemData[item] !== null;
        }).length === this.requiredFields.length;
      },
      modalTitle() {
        return `${this.$i18n.t('modal_dialogs.sales_order_position.sales_order_position')}`
      },
    },
    mounted() {
      this.measurement_values = this.measurements.filter(item => item.key !== 'bundles');
      this.salesOrderItemData = {...this.salesOrderItemDataInitial};
      this.$root.$on('openSalesOrderPositionModal', (data) => {
        (async () => {
          const params = {related_sales_order: this.id, short_view: true};
          await this.$store.cache.dispatch('generic/loadPackageTypes');
          this.salesContractItems = await this.$http.get(`sales/sales_contract_items/`, {params}).then(handlerResponse);
          this.stockProducts = [], this.saleProducts = [], this.shippingProducts = [];
          this.stockProductData = {};
          this.salesOrderItemData = {...this.salesOrderItemDataInitial};
          this.isNewSalesOrderItem = true;
          if (this.$refs['SalesOrderPositionModal']) {
            this.$refs['SalesOrderPositionModal'].show();
            this.salesOrderItemData.package_type = this.packageTypes[0];
            if (data.isPackageData) {
              this.isNewSalesOrderItem = true;
              this.salesOrderItemData = {...this.salesOrderItemData, ...data};
              this.loadStockProductData(this.salesOrderItemData.product_name);
            } else if (data.id) {
              this.isNewSalesOrderItem = false;
              this.salesOrderItemData = {...this.salesOrderItemData, ...data};
              this.loadStockProductData(this.salesOrderItemData.product_name);
            }
          }
        })();
      });
    },
    beforeDestroy() {
      this.$root.$off('openSalesOrderPositionModal');
    }
  }
</script>

<style scoped>
  .stock-product-info {
    font-size: 12px;
  }

  .floating-length-wrapper {
    padding-bottom: 7px;
  }

  .floating-length-wrapper input[type='checkbox'] {
    margin-left: 10px;
  }
</style>



// WEBPACK FOOTER //
// src/components/modals/SalesOrderPositionModal.vue